import { useState, useEffect, useRef, useMemo } from "react";
import { v5 as uuidv5 } from "uuid";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  FormControl, 
  InputLabel,
  Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from '@mui/material/Grid2';
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import PopupMenu from "../PopupMenu/PopupMenu.js";
import useAlert from '../../alert/useAlert.js';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CircularProgress from '@mui/material/CircularProgress';
import { supabase } from "../../supabaseClient.js";
import { useSelector, shallowEqual } from 'react-redux';
import axios from "axios";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useLocation } from 'react-router-dom';
import AgentChatPanel from "./AgentChatPanel.js";


const roles = ["Customer Support", "Sales", "Technical Support", "Human Resources", "Others"];
// const knowledgeBases = ["Support Knowledge Base", "Sales Knowledge Base", "Tech Knowledge Base", "HR Knowledge Base"];
// const languages = ["English", "Traditional Chinese", "Simplified Chinese"];
const voices = ["Voice 1", "Voice 2", "Voice 3"];
const memories = ["Short-term", "Long-term"];


const AgentDetail = ({ onBack, agent }) => {
  const [name, setName] = useState(agent?.name || "");
  const [role, setRole] = useState(agent?.role || "");
  const [roleName, setRoleName] = useState(agent?.roleName || "");
  const [agentKnowledgeBase, setAgentKnowledgeBase] = useState(agent?.knowledge_base_name || "");
  const [tasks, setTasks] = useState(agent?.tasks || []);
  const [prompt, setPrompt] = useState(agent?.prompt || "");
  const [voice, setVoice] = useState(agent?.voice || "");
  const [memory, setMemory] = useState(agent?.memory || "");

  const [newTask, setNewTask] = useState(false);
  const [taskName, setTaskName] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [agentQuestion, setAgentQuestion] = useState("");
  const [agentAnswer, setAgentAnswer] = useState("");
  const [gettingAnswer, setGettingAnswer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showChatPanel, setShowChatPanel] = useState(false);
  const chatPanelRef = useRef(null);

  const { setAlert } = useAlert();
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const hasMounted = useRef(false);
  const location = useLocation();

  useEffect(() => {
      if (hasMounted.current) { return; }
      getKnowledgeBases();
      hasMounted.current = true;
  }, []);

  useEffect(() => {
    if (showChatPanel && chatPanelRef.current) {
      chatPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showChatPanel]);


  // useEffect(() => {
  //   const preventUnload = (event) => {
  //     // NOTE: This message isn't used in modern browsers, but is required
  //     const message = 'Sure you want to leave?';
  //     event.preventDefault();
  //     event.returnValue = message;
  //   };
  
  //   window.addEventListener('beforeunload', preventUnload);
  
  //   return () => {
  //     window.removeEventListener('beforeunload', preventUnload);
  //   };
  // }, []);
  
  const sessionKey = useMemo(
    () => (userDetails?.id ? `${userDetails.id}_${name.replace(/ /g, "-")}` : ""),
    [userDetails?.id, name]
  );
  const sessionUuid = useMemo(
    () => (sessionKey ? uuidv5(sessionKey, uuidv5.URL) : null),
    [sessionKey]
  );

  const handleSave = async () => {
    const agentData = {
      name,
      role,
      roleName,
      knowledge_base_name: agentKnowledgeBase,
      tasks,
      prompt,
      voice,
      memory,
    };
    if (agent) {
      const dateTime = new Date().toISOString();
      const updateData = { ...agentData };
      updateData.updatedAt = dateTime;
      updateData.knowledge_base_id = knowledgeBases.find(kb => kb.title === agentKnowledgeBase)?.id || null;
      await updateAgent(agent.id, updateData);
    } 
    else {
      const insertData = { ...agentData };
      insertData.teamid = currentOrg ? currentOrg.id : null; 
      insertData.knowledge_base_id = knowledgeBases.find(kb => kb.title === agentKnowledgeBase)?.id || null;
      await saveAgent(insertData);
    }
  };

  const updateAgent = async (agentID, updateData) => {
    try {
      const { error } = await supabase.from('agents').update(updateData).eq('id', agentID);
      if (error) {
        setAlert('Failed to update the agent.', 'error');
        return;
      }
      setAlert('Agent updated successfully.', 'success');
    } catch (error) {
      console.error(error);
    }
  }

  const saveAgent = async (data) => {
    try {
      const { error } = await supabase.from('agents').insert(data);
      if (error) {
        setAlert('Failed to save the agent.', 'error');
        return;
      }
      onBack();
      setAlert('Agent saved successfully.', 'success');
    } catch (error) {
      console.error(error);
    }
  }

  const handleAddTask = async (taskName, taskDescription) => {
    const task = {
      name: taskName,
      description: taskDescription,
    };
    setTasks((tasks) => [...tasks, task]);
  };

  const handleDeleteTask = (index) => {
    const updatedTasks = tasks.filter((_, i) => i !== index);
    setTasks(updatedTasks);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTasks(items);
  }

  const deleteAgentHistory = async () => {
    try {
      const data = { session_id: sessionKey };
      await axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/delete_session_history`, data);

      if (sessionUuid) {
        const { error } = await supabase
          .from("textmessages")
          .delete()
          .eq("session_id", sessionUuid);
        if (error) {
          console.error("Error deleting Supabase chat history:", error);
        }
      }
    } catch (error) {
      console.error("Error deleting agent history", error);
      setAlert("Failed to delete agent history.", "error");
    }
  };

  const getKnowledgeBases = async () => {
    try {
      const { data, error } = await supabase.from('knowledgebase').select('*').eq('teamid', currentOrg ? currentOrg.id : '');
      if (error) {
        setAlert('Failed to fetch knowledge bases.', 'error');
        return;
      }
      setKnowledgeBases(data);
    } catch (error) {
      console.error(error);
    }
  }

  const handleToggleDescription = (index) => {
    setExpandedTasks((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  return (
    <Grid container spacing={2} sx={{ flexDirection: 'column', paddingBottom: "20px"   }}>
      <Grid size={12} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Tooltip title="Back to Consultation">
          <IconButton 
            onClick={() => {
              if(memory === "Short-term") {
                deleteAgentHistory();
              }
              onBack();
            }}>
              <ArrowBackIcon />
              <Typography variant="h6" sx={{color: "#29C0BB", marginLeft: '5px', marginRight: '10px'}}>Back</Typography>
          </IconButton>
        </Tooltip>
        <Stack  direction="row" alignItems="center" spacing={5}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Set up your agent
          </Typography>
          {agent && (
            <Button
              sx={{ borderRadius: 30, backgroundColor: "#29C0BB", border: 0 }}
              onClick={() => { setShowChatPanel(true); }}
              variant="outlined"
              startIcon={<AddIcon sx={{ color: "#fff" }} />}
            >
              <Typography variant="button" sx={{ color: "#FFFFFF" }}>
                Chat with Agent
              </Typography>
            </Button>
          )}
        </Stack>
      </Grid>
      <Grid container spacing={3} sx={{ display: "flex", flexDirection: 'row', justifyContent: "flex-start", alignItems: "center" }}>
        <TextField
          focused
          fullWidth
          label="Agent's Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2, backgroundColor: "#ffffff" }}
          placeholder="Enter agent's name"
        />
        {/* <FormControl fullWidth>
          <InputLabel id="role-label">Role</InputLabel>
          <Select
            labelId="role-label"
            id="role"
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
          >
          {roles.map((role, index) => (
            <MenuItem key={index} value={role}>
              {role}
            </MenuItem>
          ))}
        </Select>
        </FormControl> */}
        <TextField
          focused
          fullWidth
          label="Role Name"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          sx={{ mb: 2, backgroundColor: "#ffffff" }}
          placeholder="Enter role name"
        />
        <FormControl fullWidth>
          <InputLabel id="knowledge-label">Select your knowledge base(s) to connect</InputLabel>
          <Select
            labelId="knowledge-label"
            id="knowledge"
            label="Select your knowledge base(s) to connect"
            value={agentKnowledgeBase}
            onChange={(e) => setAgentKnowledgeBase(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
          >
            {knowledgeBases.map((kb, index) => (
              <MenuItem key={index} value={kb.title}>
                {kb.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mb: 2, width: '100%'}}>
          <Stack sx={{display: "flex", flexDirection: "row", alignItems: 'flex-start', justifyContent: 'space-between'}}>
            <Typography variant="h6">Task Checklist</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => { setNewTask(true); setFormSubmitted(false); setTaskName(""); setTaskDescription(""); }}
              sx={{ color: "#29C0BB" }}
            >
              Add Task
            </Button>
          </Stack>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="sections">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {tasks.map((task, index) => (
                    <Draggable
                      key={`draggable-${index}`}
                      draggableId={`draggable-${index}`}
                      index={index}
                    >
                      {(provided) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            padding: 2,
                            backgroundColor: "#FFFFFF",
                            cursor: "grab",
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", borderRadius: 2, backgroundColor: "#ffffff" }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <DragIndicatorIcon sx={{ color: '#29C0BB' }} />
                              <Typography variant="body1" sx={{fontWeight: 'bold'}}>{task.name}</Typography>
                              <IconButton onClick={() => handleToggleDescription(index)}>
                                {expandedTasks[index] ? (
                                  <ExpandLessIcon sx={{ color: '#29C0BB' }} />
                                ) : (
                                  <ExpandMoreIcon sx={{ color: '#29C0BB' }} />
                                )}
                              </IconButton>
                            </Stack>
                            <IconButton onClick={() => handleDeleteTask(index)}>
                              <DeleteIcon sx={{ color: 'red' }} />
                            </IconButton>
                          </Box>
                          {expandedTasks[index] && (
                            <Box sx={{ marginTop: 1, marginLeft: 5 }}>
                              <Typography variant="body2" sx={{ color: 'textSecondary' }}>
                                {task.description}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
        <TextField
          focused
          fullWidth
          label="Prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{ mb: 2, backgroundColor: "#ffffff" }}
          placeholder="Eg. The agent has to be polite and concise"
          multiline
          rows={10}
        />
        {/* <FormControl fullWidth>
          <InputLabel id="language-label">Language</InputLabel>
          <Select
            labelId="language-label"
            id="language"
            label="Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
          >
            {languages.map((lang, index) => (
              <MenuItem key={index} value={lang}>
                {lang}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}
        {/* <FormControl fullWidth>
          <InputLabel id="voice-label">Voice</InputLabel>
          <Select
            labelId="voice-label"
            id="voice"
            label="Voice"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
          >
            {voices.map((v, index) => (
              <MenuItem key={index} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}
        <FormControl fullWidth>
          <InputLabel id="memory-label">Memory</InputLabel>
          <Select
            labelId="memory-label"
            id="memory"
            label="Memory"
            value={memory}
            onChange={(e) => setMemory(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#ffffff", '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#29C0BB' }  }}
          >
            {memories.map((v, index) => (
              <MenuItem key={index} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box onClick={handleSave} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#ffffff", cursor: 'pointer'}}>
          {agent ? "Update" : "Save"}
        </Box>
      </Grid>
      {showChatPanel && (
        <Box ref={chatPanelRef} sx={{ width: "100%" }}>
          <AgentChatPanel
            sessionKey={sessionKey}
            agentName={name}
            roleName={roleName}
            prompt={prompt}
            tasks={tasks}
            currentOrg={currentOrg}
            knowledgeBaseId={knowledgeBases.find((kb) => kb.title === agentKnowledgeBase)?.id || null}
            backendUrl={process.env.REACT_APP_RAG_BACKEND_URL}
            whatsappUrl={process.env.REACT_APP_WHATSAPP_BACKEND_URL}
            isTestingBot={name === "Carrie"}
            memory={memory}
            userDetails={userDetails}
            onClose={() => setShowChatPanel(false)}
          />
        </Box>
      )}
      {
        newTask && (
          <PopupMenu>
            <Typography variant="h6">New Task</Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  label="Task Name"
                  variant="outlined"
                  focused
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="Enter task name" 
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                  error={formSubmitted && !taskName}
                  helperText={formSubmitted && !taskName && "Task Name is required"}
                />
                <TextField
                  required
                  fullWidth
                  label="Task Description"
                  variant="outlined"
                  focused
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  placeholder="Enter task description" 
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                  error={formSubmitted && !taskDescription}
                  helperText={formSubmitted && !taskDescription && "Task Description is required"}
                />
              </Grid>
              <Grid size={12}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>                   
                  <Box onClick={() => {setNewTask(false)}} sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer'}}>
                    Close
                  </Box>  
                  <Box onClick={() => { 
                      setFormSubmitted(true);
                      if (!taskName || !taskDescription) {
                        setAlert('Please fill in all required fields.', 'error');
                        return;
                      }
                      handleAddTask(taskName, taskDescription); 
                      setNewTask(false); 
                      setAlert('Task added successfully.', 'success');
                    }} 
                    sx={{ width: '80px', textAlign: 'center', borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#ffffff", cursor: 'pointer'}}>
                    Confirm
                  </Box>  
                </Stack>
              </Grid>
            </Grid>
          </PopupMenu>
        )
      }
    </Grid>
  );
};

export default AgentDetail;
